import { format } from 'date-fns'
import { WellDrinkingWaterFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import {
  CheckableField,
  CheckableFields,
  DateField,
  Divider,
  FormField,
  Heading,
  InputWidth,
  TextInput,
} from '@msaf/core-react'

interface WellDrinkingWaterFieldsEditInterface {
  fields: Partial<WellDrinkingWaterFieldsEdit>
  isLoading: boolean
  setValue: SetValue<WellDrinkingWaterFieldsEdit>
  validationErrors: ValidationErrors<WellDrinkingWaterFieldsEdit>
}

export default function WellDrinkingWaterEdit({
  fields,
  isLoading,
  setValue,
  validationErrors,
}: WellDrinkingWaterFieldsEditInterface) {
  return (
    <>
      <Heading isSkeleton={isLoading} level={2}>
        Drinking water supply
      </Heading>

      <Heading isSkeleton={isLoading} level={3}>
        Status
      </Heading>

      <FormField
        validationMessages={validationErrors?.supplyIsActive}
        isSkeleton={isLoading}
        labelId='supplyIsActiveLabel'
        label='Supply is active'
      >
        <CheckableFields ariaLabelledBy='supplyIsActiveLabel' isHorizontal>
          <CheckableField
            id='supplyIsActiveYes'
            name='supplyIsActive'
            describedBy='supplyIsActiveLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.supplyIsActive ?? false}
            onChange={() => setValue('supplyIsActive', true)}
          />
          <CheckableField
            id='supplyIsActiveNo'
            name='supplyIsActive'
            describedBy='supplyIsActiveLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.supplyIsActive === false}
            onChange={() => setValue('supplyIsActive', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        isSkeleton={isLoading}
        label='Supply commissioning date'
        htmlFor='supplyCommissioningDate'
        validationMessages={validationErrors?.supplyCommissioningDate}
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.supplyCommissioningDate)}
          setSelectedDate={(supplyCommissioningDate: Date) =>
            setValue(
              'supplyCommissioningDate',
              supplyCommissioningDate ? format(supplyCommissioningDate, INTERNAL_DATE_FORMAT) : '',
            )
          }
          isInvalid={!!validationErrors.supplyCommissioningDate}
        />
      </FormField>

      <FormField
        isSkeleton={isLoading}
        label='Supply decommissioning date'
        htmlFor='supplyDecommissioningDate'
        validationMessages={validationErrors?.supplyDecommissioningDate}
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.supplyDecommissioningDate)}
          setSelectedDate={(supplyDecommissioningDate: Date) =>
            setValue(
              'supplyDecommissioningDate',
              supplyDecommissioningDate ? format(supplyDecommissioningDate, INTERNAL_DATE_FORMAT) : '',
            )
          }
          isInvalid={!!validationErrors.supplyDecommissioningDate}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authorityCategory}
        isSkeleton={isLoading}
        label='Supply type'
        labelId='authorityCategoryLabel'
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='authorityCategory'
          labelledBy='authorityCategoryLabel'
          value={`${fields.authorityCategory ?? ''}`}
          setValue={({ value }) => setValue('authorityCategory', value)}
          isInvalid={!!validationErrors.authorityCategory}
        />
      </FormField>

      <Divider />

      <Heading isSkeleton={isLoading} level={3}>
        Taumata Arowai Hinekorako register
      </Heading>

      <FormField
        validationMessages={validationErrors?.isSupplyRegisteredWithAuthority}
        isSkeleton={isLoading}
        labelId='isSupplyRegisteredWithAuthorityLabel'
        label='Registered in Hinekorako'
      >
        <CheckableFields ariaLabelledBy='isSupplyRegisteredWithAuthorityLabel' isHorizontal>
          <CheckableField
            id='isSupplyRegisteredWithAuthorityYes'
            name='isSupplyRegisteredWithAuthority'
            describedBy='isSupplyRegisteredWithAuthorityLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isSupplyRegisteredWithAuthority ?? false}
            onChange={() => setValue('isSupplyRegisteredWithAuthority', true)}
          />
          <CheckableField
            id='isSupplyRegisteredWithAuthorityNo'
            name='isSupplyRegisteredWithAuthority'
            describedBy='isSupplyRegisteredWithAuthorityLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isSupplyRegisteredWithAuthority === false}
            onChange={() => setValue('isSupplyRegisteredWithAuthority', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.supplyOwnerName}
        isSkeleton={isLoading}
        label='Supply owner name'
        htmlFor='supplyOwnerName'
      >
        <TextInput
          isSkeleton={isLoading}
          id='supplyOwnerName'
          value={`${fields.supplyOwnerName ?? ''}`}
          setValue={(value) => setValue('supplyOwnerName', value)}
          isInvalid={!!validationErrors.supplyOwnerName}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authoritySupplyName}
        isSkeleton={isLoading}
        label='Supply name'
        htmlFor='authoritySupplyName'
      >
        <TextInput
          isSkeleton={isLoading}
          id='authoritySupplyName'
          value={`${fields.authoritySupplyName ?? ''}`}
          setValue={(value) => setValue('authoritySupplyName', value)}
          isInvalid={!!validationErrors.authoritySupplyName}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authoritySupplyNo}
        isSkeleton={isLoading}
        label='Supply ID'
        htmlFor='authoritySupplyNo'
      >
        <TextInput
          isSkeleton={isLoading}
          id='authoritySupplyNo'
          value={`${fields.authoritySupplyNo ?? ''}`}
          setValue={(value) => setValue('authoritySupplyNo', value)}
          isInvalid={!!validationErrors.authoritySupplyNo}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authoritySourceName}
        isSkeleton={isLoading}
        label='Source name'
        htmlFor='authoritySourceName'
      >
        <TextInput
          isSkeleton={isLoading}
          id='authoritySourceName'
          value={`${fields.authoritySourceName ?? ''}`}
          setValue={(value) => setValue('authoritySourceName', value)}
          isInvalid={!!validationErrors.authoritySourceName}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authoritySourceNo}
        isSkeleton={isLoading}
        label='Source ID'
        htmlFor='authoritySourceNo'
      >
        <TextInput
          isSkeleton={isLoading}
          id='authoritySourceNo'
          value={`${fields.authoritySourceNo ?? ''}`}
          setValue={(value) => setValue('authoritySourceNo', value)}
          isInvalid={!!validationErrors.authoritySourceNo}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.authoritySize}
        isSkeleton={isLoading}
        label='Supply population'
        labelId='authoritySizeLabel'
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='authoritySize'
          labelledBy='authoritySizeLabel'
          value={`${fields.authoritySize ?? ''}`}
          setValue={({ value }) => setValue('authoritySize', value)}
          isInvalid={!!validationErrors.authoritySize}
        />
      </FormField>

      <Divider />

      <Heading isSkeleton={isLoading} level={3}>
        Verification
      </Heading>

      <FormField
        validationMessages={validationErrors?.verifiedBy}
        isSkeleton={isLoading}
        label='Verified by'
        htmlFor='verifiedBy'
      >
        <TextInput
          isSkeleton={isLoading}
          id='verifiedBy'
          value={`${fields.verifiedBy ?? ''}`}
          setValue={(value) => setValue('verifiedBy', value)}
          isInvalid={!!validationErrors.verifiedBy}
        />
      </FormField>

      <FormField
        isSkeleton={isLoading}
        label='Verified date'
        htmlFor='verifiedDate'
        validationMessages={validationErrors?.verifiedDate}
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.verifiedDate)}
          setSelectedDate={(verifiedDate: Date) =>
            setValue('verifiedDate', verifiedDate ? format(verifiedDate, INTERNAL_DATE_FORMAT) : '')
          }
          isInvalid={!!validationErrors.verifiedDate}
        />
      </FormField>
    </>
  )
}
